import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { downloadAttachment } from "../api/attachmentsApi";
import {
  getAttachmentDisplayName,
  isUploadedAttachment,
  normalizeEventAttachments,
} from "../utils/eventAttachments";
import "./EventModal.css";
import "./EventDetailsModal.css";

const normalizeTimeFormat = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .startsWith("24")
    ? "24h"
    : "12h";

function formatEventDateRange(event) {
  const startValue = String(event?.start || "");
  const isAllDay = startValue.length === 10 || event?.allDay === true || event?.extendedProps?.all_day === true;

  if (isAllDay) {
    const startDate = new Date(`${startValue}T00:00:00`);
    return startDate.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  const startDate = new Date(startValue);
  return startDate.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatClockLabel(value, timeFormat = "12h") {
  const dt = new Date(value);
  const normalized = normalizeTimeFormat(timeFormat);

  if (normalized === "24h") {
    return dt.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  return dt
    .toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true })
    .replace(":", ".")
    .replace(" ", "")
    .toLowerCase();
}

function formatDurationLabel(minutes) {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    if (rem === 0) return `${hours} hour${hours > 1 ? "s" : ""}`;
    return `${hours} hour${hours > 1 ? "s" : ""} ${rem} min`;
  }
  return `${minutes} minutes`;
}

function parseReminderConfig(value, fallbackDate) {
  const defaults = {
    pattern: "one_time_date",
    offset_minutes: 15,
    one_time_date: fallbackDate,
  };

  if (!value || typeof value !== "string") return defaults;

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return defaults;
    return {
      ...defaults,
      ...parsed,
      offset_minutes: Number(parsed.offset_minutes ?? defaults.offset_minutes),
    };
  } catch {
    return defaults;
  }
}

function linkifyBareUrls(text) {
  if (!text) return "";

  return text.replace(/(?:https?:\/\/|www\.)[^\s<]+/g, (url, offset, fullText) => {
    const prefix = fullText.slice(Math.max(0, offset - 2), offset);
    if (prefix === "](") {
      return url;
    }

    const suffixMatch = url.match(/[).,!?:;]+$/);
    const trailing = suffixMatch ? suffixMatch[0] : "";
    const cleanUrl = trailing ? url.slice(0, -trailing.length) : url;
    const href = cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl;

    return `[${cleanUrl}](${href})${trailing}`;
  });
}

function formatRepeatLabel(eventData) {
  const rawRule = String(eventData?.extendedProps?.repeat_rule || "").trim().toLowerCase();
  if (!rawRule || rawRule === "none") {
    return eventData?.extendedProps?.series_id ? "Recurring" : "Does not repeat";
  }

  if (rawRule === "daily") return "Daily";
  if (rawRule === "weekly") return "Weekly";
  if (rawRule === "monthly") return "Monthly";
  if (rawRule === "yearly") return "Yearly";
  return rawRule.charAt(0).toUpperCase() + rawRule.slice(1);
}

const QUICK_REMINDER_OPTIONS = [0, 5, 15, 30, 60, 180, 1440, 10080];

function EventDetailsModal({ event, onClose, onEdit, onDelete, onReminderChange, onManageCategories, timeFormat = "12h" }) {
  const eventData = event || { extendedProps: {} };

  const notes = String(eventData.extendedProps?.internal_notes || "").trim();
  const invoiceNotes = String(eventData.extendedProps?.invoice_notes || "").trim();
  const location = String(eventData.extendedProps?.location || "").trim();
  const projectName = String(eventData.extendedProps?.project_name || "").trim();
  const categoryName = String(eventData.extendedProps?.category_name || "").trim();
  const repeatLabel = formatRepeatLabel(eventData);
  const attachments = normalizeEventAttachments(
    eventData.extendedProps?.attachments || eventData.attachments
  );

  let durationMinutes = 30;
  if (eventData.start && eventData.end) {
    const start = new Date(eventData.start).getTime();
    const end = new Date(eventData.end).getTime();
    durationMinutes = Math.round((end - start) / 60_000);
  }

  const startValue = String(eventData?.start || "");
  const fallbackDate = String(startValue).slice(0, 10);
  const initialReminderConfig = parseReminderConfig(eventData.extendedProps?.reminder_config || "", fallbackDate);
  const [reminderEnabled, setReminderEnabled] = useState(eventData.extendedProps?.reminder_enabled === true);
  const [reminderOffset, setReminderOffset] = useState(Number(initialReminderConfig.offset_minutes || 15));
  const [showNotesPage, setShowNotesPage] = useState(false);

  useEffect(() => {
    const nextConfig = parseReminderConfig(eventData.extendedProps?.reminder_config || "", fallbackDate);
    setReminderEnabled(eventData.extendedProps?.reminder_enabled === true);
    setReminderOffset(Number(nextConfig.offset_minutes || 15));
    setShowNotesPage(false);
  }, [event?.id, eventData.extendedProps?.reminder_enabled, eventData.extendedProps?.reminder_config, fallbackDate]);

  if (!event) return null;

  const timeDurationLine = (() => {
    if (!eventData.start || !eventData.end || startValue.length === 10) return "All day";
    const from = formatClockLabel(eventData.start, timeFormat);
    const to = formatClockLabel(eventData.end, timeFormat);
    return `${from} - ${to} (${formatDurationLabel(durationMinutes)})`;
  })();

  const mapsLink = location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
    : "";

  const pushReminderUpdate = (enabled, offset) => {
    if (typeof onReminderChange !== "function") return;
    const nextConfig = {
      ...initialReminderConfig,
      offset_minutes: Number(offset),
      one_time_date: initialReminderConfig.one_time_date || fallbackDate,
    };
    onReminderChange({
      enabled,
      config: JSON.stringify(nextConfig),
      offset: Number(offset),
    });
  };

  const markdownNotes = linkifyBareUrls(notes);

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal event-details-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="event-details-top">
          <div className="event-details-header">
            <h1>{event.title || "Untitled event"}</h1>
            <div className="event-details-datetime">{formatEventDateRange(event)}</div>
            <div className="event-details-time-line">{timeDurationLine}</div>
          </div>
        </div>

        <div className="event-details-content">
          {location && (
            <div className="event-details-section">
              <div className="event-details-section-label">Address</div>
              <a className="event-details-map-link" href={mapsLink} target="_blank" rel="noreferrer">
                {location}
              </a>
            </div>
          )}

          <div className="event-details-section">
            <div className="event-details-section-label">Reminder</div>
            <div className="event-details-reminder-row">
              <select
                className="event-details-reminder-select"
                value={reminderEnabled ? String(reminderOffset) : "__none"}
                onChange={(e) => {
                  if (e.target.value === "__none") {
                    setReminderEnabled(false);
                    pushReminderUpdate(false, reminderOffset);
                    return;
                  }
                  const offset = Number(e.target.value);
                  setReminderEnabled(true);
                  setReminderOffset(offset);
                  pushReminderUpdate(true, offset);
                }}
              >
                <option value="__none">No reminder</option>
                {QUICK_REMINDER_OPTIONS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {minutes === 0
                      ? "At event time"
                      : minutes < 60
                        ? `${minutes} minutes before`
                        : minutes === 60
                          ? "1 hour before"
                          : minutes === 10080
                            ? "1 week before"
                          : minutes === 1440
                            ? "1 day before"
                            : `${Math.floor(minutes / 60)} hours before`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="event-details-section">
            <div className="event-details-section-label">Repeat Event</div>
            <div className="event-details-section-value">{repeatLabel}</div>
          </div>

          {projectName && (
            <div className="event-details-section">
              <div className="event-details-section-label">Project</div>
              <div className="event-details-section-value">{projectName}</div>
            </div>
          )}

          {(categoryName || typeof onManageCategories === "function") && (
            <div className="event-details-section">
              <div className="event-details-section-label-row">
                <div className="event-details-section-label">Category</div>
                {typeof onManageCategories === "function" ? (
                  <button
                    type="button"
                    className="event-details-manage-link"
                    onClick={onManageCategories}
                  >
                    Manage
                  </button>
                ) : null}
              </div>
              <div className="event-details-section-value">
                {categoryName || <span className="event-details-muted">No category</span>}
              </div>
            </div>
          )}

          <div className="event-details-section">
            <div className="event-details-section-label">Attachments</div>
            <div className="event-details-section-value">
              {attachments.length > 0 ? (
                attachments.map((item, index) => (
                  <div key={`${item.storage_path || item.url}-${index}`}>
                    {isUploadedAttachment(item) ? (
                      <button
                        type="button"
                        className="event-details-attachment-button"
                        onClick={async () => {
                          try {
                            await downloadAttachment(item);
                          } catch (err) {
                            window.alert(`Could not download attachment:\n${err.message || err}`);
                          }
                        }}
                      >
                        {getAttachmentDisplayName(item)}
                      </button>
                    ) : (
                      <a href={item.url} target="_blank" rel="noreferrer">
                        {getAttachmentDisplayName(item)}
                      </a>
                    )}
                  </div>
                ))
              ) : (
                <span className="event-details-muted">No attachments</span>
              )}
            </div>
          </div>

          {notes && (
            <div className="event-details-section">
              <div className="event-details-section-label">Internal Notes</div>
              <div className="event-details-notes-preview-wrap">
                <div className="event-details-notes-preview">
                  <ReactMarkdown>{markdownNotes}</ReactMarkdown>
                </div>
              </div>
              <button
                type="button"
                className="event-details-read-more"
                onClick={() => setShowNotesPage(true)}
              >
                Read &gt;
              </button>
            </div>
          )}

          {invoiceNotes && (
            <div className="event-details-section event-details-section--invoice">
              <div className="event-details-section-label">Invoice Notes</div>
              <div className="event-details-section-value">{invoiceNotes}</div>
            </div>
          )}
        </div>

        <div className="event-details-actions">
          <button
            type="button"
            className="event-action-btn event-action-btn--danger"
            onClick={onDelete}
          >
            Delete
          </button>
          <button type="button" className="event-action-btn event-action-btn--primary" onClick={onEdit}>
            Edit
          </button>
        </div>

        {showNotesPage && (
          <div className="event-details-notes-page" role="dialog" aria-modal="true">
            <div className="event-details-notes-page-header">
              <div className="event-details-notes-page-title">Internal Notes</div>
              <button type="button" className="event-details-notes-close" onClick={() => setShowNotesPage(false)}>
                Done
              </button>
            </div>
            <div className="event-details-notes-page-body markdown-preview">
              <ReactMarkdown>{markdownNotes || "_No notes_"}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventDetailsModal;